import { TrailerAssembler } from '@contracts/order/OrderForm';
import type { TrailerWithAxlesResponse } from '@contracts/trailer/TrailerWithAxlesResponse';
import { yupResolver } from '@hookform/resolvers/yup';
import { AxlesTable } from '@src/axles/AxlesTable';
import type { Client } from '@src/clients/domain/Client';
import { Accordion } from '@src/common/components/Accordion';
import { CancelButton } from '@src/common/components/button/CancelButton';
import { SaveChangesButton } from '@src/common/components/button/SaveChangesButton';
import { Divider } from '@src/common/components/Divider';
import { Stack } from '@src/common/components/Stack';
import { theme } from '@src/common/styles/Theme';
import { AxleInformationRow } from '@src/orders/PermitFormSections/axlesConfiguration/AxleInformationRow';
import { ConditionalFieldsContext } from '@src/orders/PermitFormSections/ConditionalFieldsContext';
import { axleRowFields } from '@src/orders/schemas/AxleInformationInputsSchema';
import {
  trailerDimensionsFields,
  trailerInformationFormField,
  trailerLicensePlateFields,
} from '@src/orders/schemas/TrailerInformationInputsSchema';
import { AxleFormName } from '@src/permitField/AxleInformationFields';
import type { TrailerListing } from '@src/trailer/domain/TrailerList';
import { TrailerDimensionsForm } from '@src/trailer/service/TrailerDimensionsForm';
import { TrailerFormSchema } from '@src/trailer/TrailerFormSchema';
import { TrailerInformationForm } from '@src/trailer/TrailerInformationForm';
import { TrailerLicensePlateForm } from '@src/trailer/TrailerLicensePlateForm';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type * as yup from 'yup';

type Props = {
  client: Client;
  onSubmit: (data: yup.InferType<typeof TrailerFormSchema>) => void;
  isLoading: boolean;
  onCancel: () => void;
  prefillData?: TrailerListing;
};

type AccordionState = {
  openInformation: boolean;
  openLicensePlateDetails: boolean;
  openDimensions: boolean;
  openAxles: boolean;
};

export const TrailerPageForm = ({
  isLoading,
  onSubmit,
  onCancel,
  prefillData,
}: Props) => {
  const { t } = useTranslation();
  const [accordionState, setAccordionState] = useState<AccordionState>({
    openInformation: true,
    openLicensePlateDetails: true,
    openDimensions: true,
    openAxles: true,
  });

  const form = useForm({
    resolver: yupResolver(TrailerFormSchema),
    defaultValues: getTrailerFormValues(prefillData),
  });

  const missingAxlesError = !!form.formState.errors[AxleFormName];
  const trailerAxles = form.watch(AxleFormName);
  const axleCount = Array.isArray(trailerAxles) ? trailerAxles.length : 0;

  return (
    <FormProvider {...form}>
      <ConditionalFieldsContext.Provider
        value={{
          disabledFields: [],
        }}
      >
        <form
          onSubmit={(e) => {
            form.handleSubmit(onSubmit)(e);
            e.stopPropagation();
          }}
        >
          <Stack direction="column">
            <Accordion
              title={t('information')}
              id="trailer-information"
              expanded={accordionState.openInformation}
              onClick={(e) => {
                e.stopPropagation();
                setAccordionState((prev) => ({
                  ...prev,
                  openInformation: !prev.openInformation,
                }));
              }}
            >
              <TrailerInformationForm
                control={form.control}
                fields={Object.keys(trailerInformationFormField)}
              />
            </Accordion>
            <Divider />
            <Accordion
              id="trailer-license-plate"
              title={t('licensePlateDetails')}
              expanded={accordionState.openLicensePlateDetails}
              onClick={() =>
                setAccordionState((prev) => ({
                  ...prev,
                  openLicensePlateDetails: !prev.openLicensePlateDetails,
                }))
              }
            >
              <TrailerLicensePlateForm
                control={form.control}
                fields={Object.keys(trailerLicensePlateFields)}
              />
            </Accordion>
            <Divider />
            <Accordion
              id="trailer-dimension"
              title={t('dimensions')}
              expanded={accordionState.openDimensions}
              onClick={() =>
                setAccordionState((prev) => ({
                  ...prev,
                  openDimensions: !prev.openDimensions,
                }))
              }
            >
              <TrailerDimensionsForm
                control={form.control}
                fields={Object.keys(trailerDimensionsFields)}
              />
            </Accordion>
          </Stack>
          <Divider />
          <Accordion
            title={t('axleInformation')}
            id="tractor-axles"
            expanded={accordionState.openAxles}
            onClick={() =>
              setAccordionState((prev) => ({
                ...prev,
                openAxles: !prev.openAxles,
              }))
            }
          >
            <AxlesTable
              name={t('axleTable')}
              fields={Object.keys(axleRowFields)}
              axleCount={axleCount ?? 0}
              getValue={form.getValues}
              setValue={form.setValue}
              missingAxlesError={missingAxlesError}
            >
              {Array.from({ length: axleCount ?? 0 }).map((_, i) => (
                <AxleInformationRow
                  fields={Object.keys(axleRowFields)}
                  key={`axle-${i}-information`}
                  index={i}
                  totalAxles={axleCount ?? 0}
                  isLoading={isLoading}
                  vehicleType="TRAILER"
                  getValue={form.getValues}
                  setValue={form.setValue}
                  cumulativeAxleCount={0}
                />
              ))}
            </AxlesTable>
          </Accordion>
          <Stack
            direction="row"
            gap={theme.spacing['2']}
            justifyContent="flex-end"
          >
            <CancelButton onClick={onCancel} />
            <SaveChangesButton disabled={isLoading} />
          </Stack>
        </form>
      </ConditionalFieldsContext.Provider>
    </FormProvider>
  );
};

const getTrailerFormValues = (
  trailer: TrailerWithAxlesResponse | undefined,
): yup.InferType<typeof TrailerFormSchema> | undefined => {
  if (!trailer) return undefined;

  return TrailerAssembler.toFormData(trailer) as
    | yup.InferType<typeof TrailerFormSchema>
    | undefined;
};
